import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { Clock, CheckCircle2, XCircle, ArrowRight, CalendarCheck, Wrench, Sparkles } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export function FieldServicesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="relative z-10 bg-brand-black min-h-screen selection:bg-brand-orange/30" data-id="element-959">
      {/* 1. HERO SECTION */}
      <section className="relative pt-40 pb-32 md:pt-52 md:pb-40 overflow-hidden" data-id="element-960">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-orange/5 rounded-full blur-[120px] pointer-events-none" data-id="element-961" />

        <div className="max-w-5xl mx-auto px-6 relative z-10 text-center" data-id="element-962">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-5xl md:text-7xl font-display mb-8 tracking-tight leading-[0.95] text-balance" data-id="element-963">
            Intelligent Field Service <br className="hidden md:block" data-id="element-964" />
            <span className="text-brand-orange" data-id="element-965">
              Without the Dispatch Chaos.
            </span>
          </motion.h1>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.1,
          ease: [0.16, 1, 0.3, 1]
        }} className="max-w-3xl mx-auto space-y-6 mb-12" data-id="element-966">
            <p className="text-lg md:text-xl text-gray-400 leading-relaxed font-light max-w-2xl mx-auto" data-id="element-967">
              Stop letting manual scheduling, missing parts, and reactive
              dispatching throttle your service capacity. We deploy autonomous
              AI agents that handle triage, routing, and technician enablement
              so your team can focus on fixing the problem.
            </p>
            <p className="text-lg md:text-xl text-white font-medium" data-id="element-968">
              If your technicians are waiting on dispatch, you're losing money.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex flex-col sm:flex-row items-center justify-center gap-6" data-id="element-969">
            <a href="mailto:sales@aijentik.com" className="w-full sm:w-auto bg-brand-orange hover:bg-brand-deep-orange text-white px-8 py-4 rounded-full font-medium transition-all duration-300 shadow-[0_0_40px_rgba(219,127,74,0.3)] hover:shadow-[0_0_60px_rgba(219,127,74,0.4)] flex items-center justify-center gap-2" data-id="element-970">
              Get in Touch
              <ArrowRight className="w-4 h-4" data-id="element-971" />
            </a>
            <a href="#use-cases" className="w-full sm:w-auto text-white hover:text-brand-orange px-8 py-4 font-medium transition-colors flex items-center justify-center" data-id="element-972">
              See the Use Cases
            </a>
          </motion.div>
        </div>
      </section>

      {/* 2. INDUSTRY REALITY SECTION */}
      <section className="py-32 bg-[#050505] border-y border-white/5" data-id="element-973">
        <div className="max-w-7xl mx-auto px-6" data-id="element-974">
          <div className="flex flex-col lg:flex-row gap-16 lg:gap-24 items-center" data-id="element-975">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeUp} className="lg:w-1/2" data-id="element-976">
              <h2 className="text-4xl md:text-5xl font-display leading-[1.0] mb-6 text-balance tracking-tight" data-id="element-977">
                You're Not Just Fixing Equipment.
                <br data-id="element-978" />
                <span className="text-gray-500" data-id="element-979">
                  You're Managing Logistics.
                </span>
              </h2>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} className="lg:w-1/2" data-id="element-980">
              <motion.p variants={fadeUp} className="text-xl text-gray-400 leading-relaxed mb-10 font-light" data-id="element-981">
                Most field service businesses aren't limited by technician skill
                — they're throttled by coordination chaos. Jobs lost to
                voicemail, technicians driving across town for the wrong parts,
                and office staff drowning in scheduling calls instead of growing
                the business.
              </motion.p>

              <div className="space-y-8" data-id="element-982">
                {[{
                label: 'The Dispatch Gap',
                desc: 'After-hours calls go to voicemail. By morning, the customer has already booked someone else.'
              }, {
                label: 'The "Where Are They?" Loop',
                desc: 'Customers call the office, the office calls the tech, the tech is mid-job — nobody gets an answer.'
              }, {
                label: 'The Admin Leak',
                desc: 'Quotes sit unsent, invoices pile up, and follow-ups never happen — bleeding revenue every week.'
              }].map((point, i) => <motion.div key={i} variants={fadeUp} className="flex items-start gap-4" data-id="element-983">
                    <div className="mt-1 bg-red-500/10 p-1 rounded-full shrink-0" data-id="element-984">
                      <XCircle className="w-5 h-5 text-red-500/70" data-id="element-985" />
                    </div>
                    <div data-id="element-986">
                      <p className="text-lg text-white font-medium mb-1" data-id="element-987">
                        {point.label}
                      </p>
                      <p className="text-gray-400 font-light leading-relaxed" data-id="element-988">
                        {point.desc}
                      </p>
                    </div>
                  </motion.div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. SOLUTION SECTION (AI AGENTS) */}
      <section id="use-cases" className="py-32 relative" data-id="element-989">
        <div className="max-w-7xl mx-auto px-6" data-id="element-990">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="max-w-3xl mb-20" data-id="element-991">
            <h2 className="text-4xl md:text-5xl font-display mb-6 leading-[1.0] tracking-tight text-balance" data-id="element-992">
              Purpose-Built Agents for Field Operations.
            </h2>
            <p className="text-xl text-gray-400 font-light leading-relaxed max-w-2xl" data-id="element-993">
              We don't sell AI tools. We build autonomous digital dispatchers
              that understand your SLA requirements, access your inventory
              systems, and execute real routing without human involvement.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-8" data-id="element-994">
            {[{
            icon: <Clock className="w-6 h-6 text-brand-orange" data-id="element-995" />,
            title: 'Intelligent Triage',
            desc: 'Agents instantly categorise incoming requests, determine urgency based on SLAs, and identify required skills before a human ever sees the ticket.'
          }, {
            icon: <CalendarCheck className="w-6 h-6 text-brand-orange" data-id="element-996" />,
            title: 'Dynamic Dispatch',
            desc: 'Automatically routes the right technician based on location, skill set, and current inventory levels, optimising routes in real-time.'
          }, {
            icon: <Wrench className="w-6 h-6 text-brand-orange" data-id="element-997" />,
            title: 'Technician Enablement',
            desc: 'Provides field workers with instant access to manuals, service history, and diagnostic steps via conversational AI on their mobile device.'
          }].map((card, i) => <motion.div key={i} variants={fadeUp} className="bg-white/[0.02] border border-white/5 p-10 rounded-2xl hover:bg-white/[0.04] transition-all duration-700 ease-premium hover:-translate-y-2 hover:border-brand-orange/30 hover:shadow-[0_20px_40px_-15px_rgba(219,127,74,0.15)] group" data-id="element-998">
                <div className="bg-brand-orange/10 w-12 h-12 rounded-xl flex items-center justify-center mb-8 group-hover:bg-brand-orange/20 transition-colors duration-500" data-id="element-999">
                  {card.icon}
                </div>
                <h3 className="text-xl font-medium text-white mb-4 tracking-tight" data-id="element-1000">
                  {card.title}
                </h3>
                <p className="text-gray-400 leading-relaxed font-light" data-id="element-1001">
                  {card.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 4. TRANSFORMATION SECTION (BEFORE vs AFTER) */}
      <section className="py-32 bg-[#050505] border-y border-white/5" data-id="element-1002">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1003">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid lg:grid-cols-2 gap-12 lg:gap-0 relative" data-id="element-1004">
            <div className="hidden lg:block absolute top-0 bottom-0 left-1/2 w-px bg-gradient-to-b from-transparent via-white/10 to-transparent -translate-x-1/2" data-id="element-1005" />

            {/* Left: Traditional Model */}
            <motion.div variants={fadeUp} className="lg:pr-20" data-id="element-1006">
              <h3 className="text-sm font-bold tracking-[0.25em] text-gray-500 uppercase mb-12" data-id="element-1007">
                Traditional Model
              </h3>
              <div className="space-y-10" data-id="element-1008">
                {['After-hours emergency call goes to voicemail — customer books a competitor', 'Technician sits idle for 45 minutes waiting for the next dispatch', 'Quote sent last week — no follow-up, no conversion, no revenue'].map((text, i) => <div key={i} className="flex gap-6 opacity-60" data-id="element-1009">
                    <XCircle className="w-6 h-6 text-gray-500 shrink-0" data-id="element-1010" />
                    <p className="text-xl text-gray-400 font-light" data-id="element-1011">{text}</p>
                  </div>)}
              </div>
            </motion.div>

            {/* Right: Aijentik Model */}
            <motion.div variants={fadeUp} className="lg:pl-20" data-id="element-1012">
              <h3 className="text-sm font-bold tracking-[0.25em] text-brand-orange uppercase mb-12" data-id="element-1013">
                Aijentik Model
              </h3>
              <div className="space-y-10" data-id="element-1014">
                {['AI answers at 2 AM, books the job, and dispatches the nearest tech for morning', 'Smart routing fills every gap — techs move from job to job with zero downtime', 'Automated follow-up converts the quote within 48 hours'].map((text, i) => <div key={i} className="flex gap-6" data-id="element-1015">
                    <Sparkles className="w-6 h-6 text-brand-orange shrink-0" data-id="element-1016" />
                    <p className="text-xl text-white font-medium" data-id="element-1017">{text}</p>
                  </div>)}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 5. USE CASES SECTION */}
      <section className="py-32" data-id="element-1018">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1019">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="text-4xl md:text-5xl font-display text-center mb-20 leading-[1.0] tracking-tight text-balance" data-id="element-1020">
            What This Actually Looks Like <br className="hidden md:block" data-id="element-1021" />
            <span className="text-gray-500" data-id="element-1022">in Your Operations</span>
          </motion.h2>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-6" data-id="element-1023">
            {[{
            title: 'Automated Work Order Creation',
            desc: 'IoT alerts or customer emails are instantly converted into structured work orders with priority levels and required parts already identified.'
          }, {
            title: 'Smart Scheduling Engine',
            desc: 'Continuously monitors technician locations and job durations to dynamically adjust schedules when overruns or emergency calls occur.'
          }, {
            title: 'Automated Customer Updates',
            desc: 'Keeps customers informed with precise ETA updates and automated follow-ups after service completion, reducing inbound status calls.'
          }].map((card, i) => <motion.div key={i} variants={fadeUp} className="bg-[#0A0A0A] border border-white/10 p-10 rounded-2xl transition-all duration-700 ease-premium hover:-translate-y-2 hover:border-white/20 hover:shadow-2xl" data-id="element-1024">
                <h3 className="text-2xl font-display mb-4 tracking-tight" data-id="element-1025">
                  {card.title}
                </h3>
                <p className="text-gray-400 leading-relaxed font-light" data-id="element-1026">
                  {card.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 6. METHODOLOGY SECTION */}
      <section className="py-32 bg-[#050505] border-y border-white/5" data-id="element-1027">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1028">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="text-center mb-20" data-id="element-1029">
            <h2 className="text-4xl md:text-5xl font-display mb-6 leading-[1.0] tracking-tight text-balance" data-id="element-1030">
              No Fluff. Just Execution.
            </h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-12 relative" data-id="element-1031">
            <div className="hidden md:block absolute top-8 left-[15%] right-[15%] h-px bg-white/10" data-id="element-1032" />

            {[{
            step: '01',
            title: 'Audit',
            desc: 'We map your dispatch workflows, SLA requirements, and identify the bottlenecks causing technician downtime.'
          }, {
            step: '02',
            title: 'Build',
            desc: 'We train custom agents on your specific equipment manuals, routing logic, and inventory systems.'
          }, {
            step: '03',
            title: 'Integration',
            desc: 'We connect agents directly to your FSM software and communication channels for seamless dispatching.'
          }].map((item, i) => <motion.div key={i} variants={fadeUp} className="relative z-10 flex flex-col items-center text-center group" data-id="element-1033">
                <div className="w-16 h-16 rounded-full bg-brand-black border border-white/10 flex items-center justify-center text-xl font-display text-brand-orange mb-8 transition-all duration-500 group-hover:border-brand-orange/50 group-hover:bg-brand-orange/5 group-hover:shadow-[0_0_30px_rgba(219,127,74,0.2)]" data-id="element-1034">
                  {item.step}
                </div>
                <h3 className="text-2xl font-medium text-white mb-4 tracking-tight" data-id="element-1035">
                  {item.title}
                </h3>
                <p className="text-gray-400 font-light leading-relaxed max-w-sm" data-id="element-1036">
                  {item.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 7. FINAL CTA SECTION */}
      <section className="py-32 relative overflow-hidden" data-id="element-1037">
        <div className="absolute inset-0 bg-brand-orange/5" data-id="element-1038" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-orange/10 rounded-full blur-[100px] pointer-events-none" data-id="element-1039" />

        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center" data-id="element-1040">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="text-5xl md:text-6xl font-display mb-8 leading-[1.0] tracking-tight text-balance" data-id="element-1041">
            Stop Managing Chaos. <br data-id="element-1042" />
            <span className="text-brand-orange" data-id="element-1043">Start Scaling Service.</span>
          </motion.h2>

          <motion.p initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="text-xl text-gray-300 mb-12 font-light max-w-2xl mx-auto" data-id="element-1044">
            Let's look at your dispatch operations and identify automation
            opportunities that increase your first-time fix rate.
          </motion.p>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="flex flex-col items-center" data-id="element-1045">
            <a href="mailto:sales@aijentik.com" className="inline-block bg-white text-brand-black hover:bg-gray-100 px-10 py-5 rounded-full font-medium text-lg transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1 mb-6" data-id="element-1046">
              Contact Us — sales@aijentik.com
            </a>
            <p className="text-sm text-gray-500 font-medium" data-id="element-1047">
              We'll get back to you within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>
    </main>;
}